import { TableV2Column } from 'components/Table/TableV2';
import {
  ApplicationStage,
  SearchConversationItemWithViewInfo,
  SearchConversationsItem
} from 'modules/conversations/types';
import {
  ControlChip,
  ConversationStatusIconAndLabel,
  Documents
} from 'modules/conversations/components';
import { Box, Typography } from '@mui/material';
import { SearchableConversationSortableFields } from 'generated-graphql/types';
import { ConversationFilterIds } from '../constants/enums';
import { toPercentage } from 'utils/calculations';
import { formatDateFromNow, formatToUSDate } from 'utils/datetime';
import { screeningStatusFilterOptions } from '../constants/screeningStatusFilterOptions';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { ReactNode, useMemo } from 'react';
import { ActionOption, ActionPopover } from 'components/ActionPopover';
import { statusFilterOptions } from '../constants/statusFilterOptions';
import { ScreeningStatusChip } from 'modules/conversations/components/ScreeningStatusChip';
import { CalendarMonth } from '@mui/icons-material';
import { controlFilterOptions } from '../constants/controlFilterOptions';
import { ExpandableText } from 'common/ui/ExpandableText';
import { CalendarMeetingStatusChip } from 'modules/calendar/components/CalendarMeetingChip';
import { hasMoreTimeRequested } from '../components/ConversationStatusIconAndLabel';
import { MoreTimeRequestedBadge } from '../components/MoreTimeRequestedBadge';
import { stageOptions } from '../constants/stageOptions';
import { BouncingDots } from 'common/ui/BouncingDots';
import { IconTooltip } from 'common/ui';
import { ApplicationStageMapper } from './applicationStageMapper';
import { getConversationTagsColumn } from './getConversationTagsColumn';
import { CampaignType } from 'services/campaign/types';

const CandidateAction = ({
  conversationId,
  hasCalendarMeeting,
  onUpdateApplicationStage,
  onViewConversation,
  onViewCalendarMeetingDetails
}: {
  conversationId: string;
  hasCalendarMeeting: boolean;
  onUpdateApplicationStage: () => void;
  onViewConversation: () => void;
  onViewCalendarMeetingDetails: () => void;
}) => {
  const rowActionsOptions = useMemo<ActionOption[]>(() => {
    const options: ActionOption[] = [];

    options.push({
      label: 'View',
      onClick: onViewConversation,
      icon: <VisibilityIcon />
    });

    options.push({
      label: 'Change stage',
      onClick: onUpdateApplicationStage,
      icon: <SwapHorizIcon />
    });

    if (hasCalendarMeeting) {
      options.push({
        label: 'Meeting details',
        onClick: onViewCalendarMeetingDetails,
        icon: <CalendarMonth />
      });
    }

    return options;
  }, [conversationId]);

  return (
    <Box sx={{ display: 'flex' }}>
      <ActionPopover id={conversationId} options={rowActionsOptions} />
    </Box>
  );
};

export const getConversationColumns = (
  openConversationDetailsDrawer: (
    id: string,
    conversation: SearchConversationsItem
  ) => void,
  openUpdateApplicationStageDrawer: (
    conversation: SearchConversationsItem
  ) => void,
  setAttachmentModalData: (
    data: {
      conversationId: string;
      recipientName: string;
    } | null
  ) => void,
  onViewCalendarMeetingDetails: (conversation: SearchConversationsItem) => void,
  tagsFilterComponent: ReactNode,
  onTagClick: (tag: string) => void,
  selectedTags: string[],
  campaignType?: CampaignType | null
) => {
  const columns: TableV2Column<SearchConversationItemWithViewInfo>[] = [
    {
      cell: (row) =>
        row.viewInfo ? (
          <IconTooltip title="Viewing">
            <Box>
              <BouncingDots />
            </Box>
          </IconTooltip>
        ) : (
          <ConversationStatusIconAndLabel
            status={row.status}
            needsReview={!!row.needsReview}
            unresolvedNeedsReviewReasons={row.unresolvedNeedsReviewReasons}
            errorCode={row.errorCode}
            campaignType={campaignType}
            meetingBookingStatus={row.meetingBookingStatus}
          />
        ),
      filterable: true,
      filterId: ConversationFilterIds.status,
      filterOptions: statusFilterOptions,
      center: true,
      width: '52px'
    },
    {
      name: 'Candidate',
      cell: (row) => (
        <Typography
          variant="body1"
          color="primary.main"
          sx={{
            textDecoration: 'underline'
          }}
          onClick={() => openConversationDetailsDrawer?.(row.id, row)}
          component="a"
        >
          {row.participantFirstName || row.participantLastName
            ? `${row.participantFirstName || ''} ${
                row.participantLastName || ''
              }`.trim()
            : 'Unknown'}
        </Typography>
      ),
      sortable: true,
      id: SearchableConversationSortableFields.ParticipantFirstName,
      sortField: SearchableConversationSortableFields.ParticipantFirstName,
      minWidth: '100px'
    },
    {
      name: 'Status',
      cell: (row) => (
        <ScreeningStatusChip
          status={row.status}
          campaignType={campaignType}
          meetingBookingStatus={row.meetingBookingStatus}
        />
      ),
      filterable: true,
      filterId: ConversationFilterIds.screeningStatus,
      filterOptions: screeningStatusFilterOptions,
      id: ConversationFilterIds.screeningStatus
    },
    {
      name: 'Score',
      sortable: true,
      cell: (row) =>
        row.preferredAnswersScore !== null &&
        row.preferredAnswersScore !== undefined
          ? toPercentage(row.preferredAnswersScore)
          : '-',
      id: SearchableConversationSortableFields.PreferredAnswersScore,
      sortField: SearchableConversationSortableFields.PreferredAnswersScore
    },
    {
      name: 'Meeting',
      cell: (row) =>
        hasMoreTimeRequested(row.unresolvedNeedsReviewReasons) ? (
          <MoreTimeRequestedBadge />
        ) : (
          <CalendarMeetingStatusChip
            status={row.meetingBookingStatus}
            noteTakerStatus={row.calendarBooking?.noteTakerStatus}
          />
        )
    },
    {
      name: 'Stage',
      cell: (row) =>
        row.applicationStageUpdates?.items.length ? (
          <IconTooltip
            title={`Updated ${formatToUSDate(
              row.applicationStageUpdates.items[
                row.applicationStageUpdates?.items.length - 1
              ]?.stageUpdatedAt || ''
            )}`}
          >
            <Box>
              <Typography>
                {!row.applicationStage
                  ? 'No stage'
                  : ApplicationStageMapper[
                      row.applicationStage as ApplicationStage
                    ]}
              </Typography>
            </Box>
          </IconTooltip>
        ) : (
          <Typography>
            {!row.applicationStage
              ? 'No stage'
              : ApplicationStageMapper[
                  row.applicationStage as ApplicationStage
                ]}
          </Typography>
        ),
      filterable: true,
      filterId: ConversationFilterIds.applicationStage,
      id: ConversationFilterIds.applicationStage,
      filterOptions: stageOptions
    },
    getConversationTagsColumn<SearchConversationItemWithViewInfo>(
      tagsFilterComponent,
      onTagClick,
      selectedTags
    ),
    {
      name: 'Last activity',
      selector: (row) => formatDateFromNow(row.updatedAt),
      sortable: true,
      id: SearchableConversationSortableFields.UpdatedAt,
      sortField: SearchableConversationSortableFields.UpdatedAt
    },
    {
      name: 'Replies',
      selector: (row) => `${row.repliesCount || 0}`,
      sortable: true,
      id: SearchableConversationSortableFields.RepliesCount,
      sortField: SearchableConversationSortableFields.RepliesCount
    },
    {
      name: 'Attachments',
      cell: (row) => (
        <Documents
          attachments={row.attachments}
          onClick={() => {
            setAttachmentModalData?.({
              conversationId: row.id,
              recipientName:
                row.participantFirstName || row.participantLastName
                  ? `${row.participantFirstName || ''} ${
                      row.participantLastName || ''
                    }`.trim()
                  : 'unknown'
            });
          }}
        />
      )
    },
    {
      name: 'Source',
      // Lever (and some other ATS providers) emit application-source strings
      // that run several lines long. Truncate in the cell with a Show
      // more / Show less toggle so the column doesn't blow out the row height.
      cell: (row) =>
        row.applicationSource ? (
          <ExpandableText text={row.applicationSource} characterLimit={40} />
        ) : (
          '-'
        ),
      sortable: true,
      id: SearchableConversationSortableFields.ApplicationSource,
      sortField: SearchableConversationSortableFields.ApplicationSource
    },
    {
      name: 'Control',
      cell: (row) => <ControlChip isHumanTakeover={row.isHumanTakeover} />,
      filterable: true,
      filterId: ConversationFilterIds.control,
      filterOptions: controlFilterOptions
    },
    {
      isAction: true,
      cell: (row) => {
        return (
          <CandidateAction
            conversationId={row.id}
            hasCalendarMeeting={!!row.calendarBookingId}
            onUpdateApplicationStage={() =>
              openUpdateApplicationStageDrawer?.(row)
            }
            onViewConversation={() =>
              openConversationDetailsDrawer?.(row.id, row)
            }
            onViewCalendarMeetingDetails={() =>
              onViewCalendarMeetingDetails?.(row)
            }
          />
        );
      }
    }
  ];
  return columns;
};
